import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ContentChildren,
  signal,
  OnInit,
  ViewChild,
  Self,
  AfterViewChecked,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { IonicModule, IonInput } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { SelectItemComponent } from './components/select-item/select-item.component';
import { Subscription } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { EventData, SelectItem } from './types';

@Component({
  selector: 'app-select-search-popover',
  templateUrl: './select-search-popover.component.html',
  styleUrls: ['./select-search-popover.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule, ReactiveFormsModule],
})
export class SelectSearchPopoverComponent<T>
  implements OnInit, ControlValueAccessor, AfterViewChecked
{
  constructor(@Self() public ngControl: NgControl) {
    ngControl.valueAccessor = this;
  }

  @Input() placeholder = '';
  @Input() label = '';
  @Input() formControlName = '';
  @Input() disabled = false;
  @Input() itemEqualityFn: (selectedItem: T, listItem: T) => boolean;
  @Input() fill?: 'clear' | 'outline' | 'solid';
  @Input() mode?: 'ios' | 'md';
  @Input() inputClassName = '';

  @Output() searchChanged = new EventEmitter<string>();
  @Output() valueSelected = new EventEmitter<string>();
  @Output() itemSelected = new EventEmitter<T>();
  @Output() willDismiss = new EventEmitter<void>();

  @ContentChildren(SelectItemComponent) items!: QueryList<SelectItem<T>>;
  @ViewChild('searchInput') input!: IonInput;

  control: FormControl;

  isOpen = signal(false);

  displayLabel = signal('');

  id = Math.random().toString(36).substring(2, 15);

  private subscriptions: { [key: string]: Subscription } = {};

  ngOnInit(): void {
    this.control = this.ngControl.control as FormControl;
  }

  ngAfterViewChecked() {
    if (!this.items) {
      return;
    }

    this.setClickListeners(this.items);

    this.items.changes.subscribe((items) => {
      this.setClickListeners(items);
    });

    const formValue = this.control.value;

    if (formValue && !this.displayLabel()) {
      this.setDisplayLabel(formValue);
    }
  }

  setClickListeners(items: QueryList<SelectItem<T>>) {
    items.forEach((item) => {
      const subscription = this.subscriptions[item.value];

      if (subscription) {
        subscription.unsubscribe();
      }
    });

    this.subscriptions = {};

    items.forEach((item: SelectItem<T>) => {
      this.subscriptions[item.value] = item.clickEvent.subscribe((event) =>
        this.itemClicked(event),
      );
    });
  }

  itemClicked(data: EventData<T>) {
    this.closePopover();

    const value = data.item ? data.item : data.value;

    if (data.item) {
      this.itemSelected.emit(data.item);
    } else {
      this.valueSelected.emit(data.value);
    }

    this.displayLabel.set(data.label);
    this.control?.setValue(value);
  }

  onSearchChanged(event: Event) {
    const searchInput = event.target as HTMLInputElement;
    this.searchChanged.emit(searchInput.value);
  }

  openPopover = () => {
    if (this.disabled) {
      return;
    }

    this.isOpen.set(true);
  };

  /**
   * The trigger is a display-only input, so opening has to be wired up for the
   * keyboard by hand: Enter/Space activate it like a button, ArrowDown opens it
   * like a native select. Without this the control can only be reached and
   * opened with the mouse.
   */
  onTriggerKeydown = (event: KeyboardEvent) => {
    if (this.disabled || this.isOpen()) {
      return;
    }

    const opensPopover =
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'ArrowDown';

    if (opensPopover) {
      event.preventDefault();
      this.openPopover();
    }
  };

  closePopover = () => {
    this.isOpen.set(false);
    this.willDismiss.emit();
  };

  onDidPresent = () => {
    this.input.setFocus();
  };

  getClasses() {
    if (!this.control) return '';

    const classes: string[] = [];

    if (this.control.touched) classes.push('ng-touched');
    if (this.control.status === 'VALID') classes.push('ng-valid');
    if (this.control.status === 'INVALID') classes.push('ng-invalid');

    return classes.join(' ');
  }

  writeValue = (value: string) => {
    if (!value) {
      this.displayLabel.set('');

      return;
    }

    if (!this.items) {
      return;
    }

    this.setDisplayLabel(value);
  };

  setDisplayLabel(value: string | T) {
    let item = this.items.find((item) => item.value === value);

    if (!item && this.itemEqualityFn) {
      item = this.items.find(({ item }) => {
        return this.itemEqualityFn(value as T, item as T);
      });
    }

    if (item) {
      this.displayLabel.set(item.label || item.value);
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  registerOnChange = (fn: any) => {};
  registerOnTouched = (fn: any) => {};
}
